// Formatierungs-Helfer fuer UI. Bewusst minimal in SPEC-006 —
// wird erweitert, wenn wir echte Daten anzeigen.

import type { CurrentUser, SessionSummary } from '@/types/domain';

export function userLabel(u: Pick<CurrentUser, 'display_name' | 'nc_username'>): string {
  return u.display_name?.trim() || u.nc_username;
}

export function sessionLabel(s: SessionSummary): string {
  return s.display_name?.trim() || s.username;
}

// --- Datum -----------------------------------------------------------
//
// Die Anwendung zeigt Datumsangaben durchgehend deutsch (TT.MM.JJJJ).
// Vorher stand an manchen Stellen das ISO-Format aus der Datenbank —
// fuer die tägliche Arbeit am Tresen die falsche Schreibweise (B72).
//
// Eingang ist entweder ein reines Kalenderdatum ("2026-08-07") oder ein
// Zeitstempel aus der Datenbank ("2026-08-07 14:32:11", UTC). Beides
// wird unterschieden, weil ein Kalenderdatum NICHT in Ortszeit
// umgerechnet werden darf — sonst springt es abends um einen Tag
// (CLAUDE.md, Fallstrick 6).

function parseDatum(wert: string): Date | null {
  const t = wert.trim();
  if (t === '') return null;
  // Reines Kalenderdatum: verbatim zerlegen, keine Zeitzone im Spiel.
  const nurDatum = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (nurDatum) {
    return new Date(Number(nurDatum[1]), Number(nurDatum[2]) - 1, Number(nurDatum[3]));
  }
  const d = new Date(t.includes('T') ? t : t.replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

/** "2026-08-07" → "07.08.2026". Ungültiges kommt unverändert zurück. */
export function datumDe(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = parseDatum(wert);
  if (d === null) return wert;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Kurzform ohne Jahr, für Badges: "07.08." */
export function datumKurzDe(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = parseDatum(wert);
  if (d === null) return wert;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

/**
 * Kurzform, die das Jahr nur nennt, wenn es nicht das laufende ist:
 * "07.08." — aber "01.01.2020" (B86).
 *
 * Ohne das las sich eine seit Jahren liegengebliebene Bestellung wie eine
 * von vorgestern.
 */
export function datumKompaktDe(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = parseDatum(wert);
  if (d === null) return wert;
  return d.getFullYear() === new Date().getFullYear() ? datumKurzDe(wert) : datumDe(wert);
}

/** Datum mit Uhrzeit, für Tooltips: "07.08.2026, 14:32". */
export function datumZeitDe(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = parseDatum(wert);
  if (d === null) return wert;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

/**
 * Heutiges Kalenderdatum als "YYYY-MM-DD" — aus der lokalen Zeit des
 * Geräts, nicht aus UTC. `toISOString().slice(0,10)` läge in deutscher
 * Sommerzeit zwischen Mitternacht und 02 Uhr einen Tag zurück
 * (CLAUDE.md, Fallstrick 6).
 */
export function heuteIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Abstand in Kalendertagen zu heute. Positiv = Vergangenheit.
 *
 * Kalendarisch, nicht in Sekunden gerechnet: 30 Stunden können je nach
 * Uhrzeit „gestern“ ODER „vorgestern“ sein. Die verstrichene Zeit zu
 * teilen ergibt in einem Drittel der Fälle den falschen Tag (B85) — und
 * wich damit von der Dashboard-Spalte ab, die serverseitig mit DATEDIFF
 * rechnet. Alle Tagesangaben der Oberfläche hängen jetzt hier dran.
 */
export function kalendertageSeit(wert: string | null | undefined): number | null {
  if (!wert) return null;
  const d = parseDatum(wert);
  if (d === null) return null;
  const heute = new Date();
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate());
  return Math.round((b - a) / 86_400_000);
}

/**
 * Relative Zeitangabe für Verlauf und Kommunikation: "gerade eben",
 * "vor 12 Min", "vor 3 Std", "gestern", danach das deutsche Datum (B65).
 *
 * Relativ ist beim Blick auf den letzten Eintrag hilfreich, taugt aber
 * nicht als Beleg — deshalb gehört an jede Stelle, die das hier benutzt,
 * `title={datumZeitDe(...)}` als absolute Angabe daneben.
 */
export function relativZeitDe(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = parseDatum(wert);
  if (d === null) return wert;
  const sek = Math.round((Date.now() - d.getTime()) / 1000);
  // Kleine Uhrenabweichungen zwischen Server und Tablet nicht als
  // Zukunft ausgeben.
  if (sek < 60) return 'gerade eben';
  const tage = kalendertageSeit(wert) ?? 0;
  if (tage <= 0) {
    // Heute: Stunden und Minuten sind die nützlichere Angabe.
    return sek < 3600 ? `vor ${Math.round(sek / 60)} Min` : `vor ${Math.round(sek / 3600)} Std`;
  }
  if (tage === 1) return 'gestern';
  return datumDe(wert);
}

/**
 * Tage seit dem zugesagten Abholdatum. Positiv = überfällig.
 */
export function tageUeberfaellig(zugesagt: string | null | undefined): number | null {
  return kalendertageSeit(zugesagt);
}

// Wortlaute für Tagesabstände. Stehen hier und nicht bei den Farben,
// damit es für Zeitangaben eine Quelle gibt und nicht zwei (B85).

/** "heute", "gestern", "vor 22 Tagen" */
export function formatTageSeit(tage: number): string {
  if (tage <= 0) return 'heute';
  if (tage === 1) return 'gestern';
  return `vor ${tage} Tagen`;
}

/** "heute", "morgen", "in 3 Tagen", "überfällig (vor 2 Tagen)" */
export function formatTageBis(tage: number): string {
  if (tage === 0) return 'heute';
  if (tage === 1) return 'morgen';
  if (tage > 0) return `in ${tage} Tagen`;
  if (tage === -1) return 'gestern überfällig';
  return `überfällig (vor ${Math.abs(tage)} Tagen)`;
}

/** Deutsche Mehrzahl ohne Zahlwort-Akrobatik: "1 Bestellung" / "5 Bestellungen". */
export function anzahl(n: number, einzahl: string, mehrzahl: string): string {
  return `${n} ${n === 1 ? einzahl : mehrzahl}`;
}
